/**
 * The account piece (web-studio-sites D15): a custom site's own header places
 * it beside her other controls. Sign in with Telegram for a visitor; signed in,
 * the messages button with its unread dot and the client's initials opening the
 * account menu (her name, Signed in with Telegram, Bookings, Messages, Profile,
 * Sign out). Its rules are the studio header's, one hook for both (`useAccount`).
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'account',
  description:
    "Sign in with Telegram for a visitor; signed in, the Messages button with its unread dot and the client's initials opening a menu with their name, Bookings, Messages, Profile and Sign out. Placed in the site's own header.",
  placeable: true,
  options: {},
  parts: {
    signIn: { description: 'The Sign in with Telegram button.', required: false },
    messages: { description: 'Signed in: the Messages button, opening and closing the messages panel.', required: false },
    messagesBadge: { description: "The dot on the Messages button while the studio has messages the client has not read; hidden when there are none.", required: false },
    avatar: { description: "Signed in: the client's initials, opening the account menu.", required: false, states: ['open'] },
    menu: { description: "The account menu under the initials: the client's name, then Bookings, Messages, Profile and Sign out.", required: false },
    menuHead: { description: 'The top of the menu: the client\'s name and Signed in with Telegram.', required: false },
    menuName: { description: "The client's name, at the top of the menu.", required: false },
    menuNote: { description: 'Signed in with Telegram, under the name.', required: false },
    menuBookings: { description: 'Bookings, in the account menu.', required: false },
    menuMessages: { description: 'Messages, in the account menu: opens the messages panel.', required: false },
    menuProfile: { description: 'Profile, in the account menu.', required: false },
    signOut: { description: 'Sign out, in the account menu.', required: false },
    signOutError: { description: 'Under Sign out when it did not go through: the client is still signed in.', required: false },
  },
  actions: {
    signIn: { description: "Opens Telegram's sign-in window; the visitor stays on the page they were on.", args: [] },
    toggleMenu: { description: 'Opens or closes the account menu.', args: [{ name: 'open', kind: 'value', type: 'boolean', optional: true }] },
    toggleMessages: { description: 'Opens the messages panel, or closes it when open.', args: [] },
    openAccount: { description: "Opens the client's Bookings or Profile page.", args: [{ name: 'page', kind: 'value', type: 'string', values: ['bookings', 'profile'] }] },
    signOut: { description: 'Signs out on this website; the page stays where it is.', args: [] },
  },
  events: {
    signInPressed: 'A visitor pressed Sign in with Telegram.',
    signedOut: 'The client signed out.',
    messagesToggled: 'The client opened or closed the messages panel from the account piece.',
    accountOpened: 'The client opened Bookings or Profile from the account menu.',
  },
});

/** What the account piece knows. */
export interface AccountState {
  /** Who is here: loading until known, then signed out or signed in. */
  readonly session: 'loading' | 'signedOut' | 'signedIn';
  /** The signed-in client: their name and initials; null when signed out. */
  readonly client: { readonly name: string; readonly initials: string } | null;
  /** True once Sign in can open Telegram's window (its one-time value and library are ready). */
  readonly signInReady: boolean;
  /** The last sign-in attempt: idle, pending, error (it did not go through) or unavailable (the studio has no bot yet). */
  readonly signIn: 'idle' | 'pending' | 'error' | 'unavailable';
  /** True while the account menu is open. */
  readonly menuOpen: boolean;
  /** True after a Sign out the server did not take: the client is still signed in and may try again. */
  readonly signOutFailed: boolean;
  /** Signed in: the studio's secure messages the client has not read; 0 when none. */
  readonly unread: number;
  /** True while the messages panel is open. */
  readonly messagesOpen: boolean;
}

/** What a drawing can ask the account piece to do. */
export interface AccountActions {
  /** Sign in with Telegram, staying on this page signed in. */
  readonly signIn: () => void;
  /** Open or close the account menu; `false` closes it. */
  readonly toggleMenu: (open?: boolean) => void;
  /** Open the messages panel, or close it when open; the account menu closes. */
  readonly toggleMessages: () => void;
  /** Open the client's Bookings or Profile page; the account menu closes. */
  readonly openAccount: (page: 'bookings' | 'profile') => void;
  /** Sign out on this website. */
  readonly signOut: () => void;
}

/** What the account piece tells its host. */
export interface AccountEvents {
  /** A visitor pressed Sign in with Telegram. */
  readonly signInPressed: Record<string, never>;
  /** The client signed out. */
  readonly signedOut: Record<string, never>;
  /** The messages panel opened (`open` true) or closed from the account piece. */
  readonly messagesToggled: { readonly open: boolean };
  /** The client opened Bookings or Profile from the account menu. */
  readonly accountOpened: { readonly page: 'bookings' | 'profile' };
}
